import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

/**
 * Phase 5.X-v1-parity-polish — V1's "Done — Review Classifications" milestone CTA
 * ported as a dismissable dashboard card.
 *
 * Surfaces when any submission is in the `Confirm` canonical status
 * ("Review and confirm classifications" per `web/src/constants/status.ts`).
 * V1 keyed off per-document state (classified-but-not-confirmed); V2's
 * `Submissions.Status` field already aggregates that signal at the
 * submission level via the workflow state machine — `Confirm` is exactly
 * the slot V1 was trying to flag.
 *
 * Dismissal: localStorage stores the count at dismiss-time. If the current
 * count exceeds the stored count, the CTA re-appears (new submission(s)
 * entered Confirm since last dismiss). This keeps the UX from nagging on
 * the same stable set while still surfacing genuinely-new work.
 */

const DISMISS_LS_KEY = 'reviewClassificationsCTA.dismissedCount';

function loadDismissedCount(): number {
  try {
    const raw = localStorage.getItem(DISMISS_LS_KEY);
    if (raw === null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function saveDismissedCount(n: number): void {
  try {
    localStorage.setItem(DISMISS_LS_KEY, String(n));
  } catch {
    /* ignore — Safari private mode etc. */
  }
}

export interface ReviewClassificationsCTAProps {
  /** Count of submissions currently in `Confirm` status. */
  readonly confirmCount: number;
  /** ID of one submission in Confirm (used as the "Review now" deep-link target). */
  readonly firstConfirmSubmissionId: string | null;
}

export function ReviewClassificationsCTA({
  confirmCount,
  firstConfirmSubmissionId,
}: ReviewClassificationsCTAProps): ReactElement | null {
  const [dismissedCount, setDismissedCount] = useState<number>(loadDismissedCount);

  if (confirmCount === 0) return null;
  if (confirmCount <= dismissedCount) return null;
  // Dismissable only when there's a concrete target to link to.
  if (firstConfirmSubmissionId === null) return null;

  const handleDismiss = (): void => {
    saveDismissedCount(confirmCount);
    setDismissedCount(confirmCount);
  };

  const noun = confirmCount === 1 ? 'submission' : 'submissions';
  const verb = confirmCount === 1 ? 'is' : 'are';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="review-classifications-cta"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '12px 16px',
        marginBottom: 16,
        background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdfa 100%)',
        border: '1px solid #6ee7b7',
        borderRadius: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{ fontSize: 20, lineHeight: 1 }} aria-hidden="true">
          ✓
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#065f46' }}>
            {confirmCount} {noun} {verb} ready for classification review
          </div>
          <div style={{ fontSize: 12, color: '#047857', marginTop: 2 }}>
            All documents have been classified — confirm them to unlock compilation.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <Link
          to={`/submissions/${firstConfirmSubmissionId}`}
          style={{
            padding: '6px 14px',
            background: '#059669',
            color: '#fff',
            borderRadius: 6,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
          data-testid="review-classifications-cta-review"
        >
          Review now
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss review-classifications notice"
          style={{
            padding: '6px 12px',
            background: 'transparent',
            color: '#065f46',
            border: '1px solid #6ee7b7',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
          }}
          data-testid="review-classifications-cta-dismiss"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
